/**
 * VersionHistoryModal — every version of one versioned CRM thing (study
 * overview / script / other text artifacts, prescreen, form, review criteria,
 * prompt, flow), what changed between any two of them, and who changed what.
 *
 * Reads `GET /api/crm/version-history` fresh on every open. Layout is a split
 * pane sized to fit a ~700px-tall laptop viewport: the version list on the left
 * (stacked on top below `lg:`), and View / Compare / Audit trail tabs on the
 * right. Only the inner panes scroll.
 *
 * Portaled to <body> (with the Inter class re-applied) so a trigger that sits
 * inside a transformed card or a clickable row can't trap or re-trigger it:
 * clicks and keystrokes inside never bubble back to the React tree around the
 * trigger (Escape still reaches the Modal's document listener).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode, SyntheticEvent } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeftRight, Check, Copy, GitCompareArrows, History, Info, Loader2 } from 'lucide-react';
import api from '../../../services/api';
import Modal from '../forms/Modal';
import MarkdownText from '../MarkdownText';
import Alert from '../ui/Alert';
import { dateTime, relTime } from '../../lib';
import { fieldCls, iconBtnCls, secondaryBtnCls } from '../../../ui/styles';
import VersionDiffView from './VersionDiffView';
import type {
  VersionAuditEvent,
  VersionHistoryEntry,
  VersionHistoryKind,
  VersionHistoryParams,
  VersionHistoryResponse,
} from './types';

export interface VersionHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  kind: VersionHistoryKind;
  params: VersionHistoryParams;
  /** Fallback title until the server's `title` arrives. */
  title?: string;
  /** Version to preselect (a `VersionHistoryEntry.id`). */
  initialVersionId?: string;
}

type Tab = 'view' | 'compare' | 'audit';

const TABS: { id: Tab; label: string }[] = [
  { id: 'view', label: 'View' },
  { id: 'compare', label: 'Compare' },
  { id: 'audit', label: 'Audit trail' },
];

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

const btnCls = `${secondaryBtnCls} disabled:cursor-not-allowed disabled:opacity-50`;

const DERIVED_HINT =
  'Reconstructed from the version record — this change pre-dates detailed audit logging, so only what the version itself recorded is shown.';

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

/** "Jun 5" (year appended when it isn't this year). */
function shortDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(d.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }),
  });
}

/** "Published Jun 5, 2:39 PM by Jane Doe" / "Created … by …" / null. */
function metaLine(v: VersionHistoryEntry): string | null {
  if (v.published_at) return `Published ${dateTime(v.published_at)}${v.published_by_name ? ` by ${v.published_by_name}` : ''}`;
  if (v.created_at) return `Created ${dateTime(v.created_at)}${v.created_by_name ? ` by ${v.created_by_name}` : ''}`;
  return null;
}

/** Select option text: "v3 — Previously published — Jun 5". */
function optionLabel(v: VersionHistoryEntry): string {
  return [v.label, v.state_label, shortDate(v.published_at || v.created_at)].filter(Boolean).join(' — ');
}

/** relTime reads "3h"/"2d" for the last week and a bare date after that. */
function recentAgo(iso: string): string | null {
  const r = relTime(iso);
  if (r === 'now') return 'just now';
  return /^\d+[mhd]$/.test(r) ? `${r} ago` : null;
}

/** The newest version that isn't a discarded draft (the list's top otherwise). */
function defaultNewest(versions: VersionHistoryEntry[]): VersionHistoryEntry | null {
  return versions.find((v) => v.state !== 'discarded') ?? versions[0] ?? null;
}

/**
 * What "the one before it" means by default: the next live or previously
 * published version (a stranded old draft between two publishes is skipped),
 * then any non-discarded version, then anything.
 */
function defaultPrevious(versions: VersionHistoryEntry[], newestId: string | null): VersionHistoryEntry | null {
  const rest = versions.filter((v) => v.id !== newestId);
  return rest.find((v) => v.state === 'live' || v.state === 'previous')
    ?? rest.find((v) => v.state !== 'discarded')
    ?? rest[0]
    ?? null;
}

const PILL_CLS: Record<VersionHistoryEntry['state'], string> = {
  live: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  draft: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  previous: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  discarded: 'bg-gray-100 italic text-gray-500 dark:bg-gray-700/60 dark:text-gray-400',
};

function StatePill({ entry }: { entry: VersionHistoryEntry }) {
  return (
    <span
      className={`inline-flex flex-shrink-0 items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${PILL_CLS[entry.state] || PILL_CLS.previous}`}
    >
      {entry.state_label}
    </span>
  );
}

/** The View pane's provenance line: published/created, by whom, and origin. */
function VersionMeta({ entry }: { entry: VersionHistoryEntry }) {
  const parts: string[] = [];
  const meta = metaLine(entry);
  if (meta) parts.push(meta);
  // Drafted by one person, published by another: name both.
  if (entry.published_at && entry.created_by_name && entry.created_by_name !== entry.published_by_name) {
    parts.push(`created by ${entry.created_by_name}`);
  }
  if (entry.origin) parts.push(entry.origin);
  if (!parts.length) return null;
  return (
    <p className="mb-3 text-xs text-gray-600 dark:text-gray-400">
      {parts.map((part, i) => (
        <span key={i}>
          {i > 0 && <span className="mx-1.5 text-gray-300 dark:text-gray-600" aria-hidden="true">·</span>}
          {part}
        </span>
      ))}
    </p>
  );
}

function VersionChip({ version }: { version: number }) {
  return (
    <span className="inline-flex items-center whitespace-nowrap rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium tabular-nums text-gray-700 dark:bg-gray-700 dark:text-gray-300">
      v{version}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Panes
// ---------------------------------------------------------------------------

function CenteredNote({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
      <History className="mb-3 h-10 w-10 text-gray-300 dark:text-gray-600" aria-hidden="true" />
      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{title}</p>
      {children && <p className="mt-1 max-w-sm text-sm text-gray-600 dark:text-gray-400">{children}</p>}
    </div>
  );
}

function AuditTrail({ events, truncated }: { events: VersionAuditEvent[]; truncated: boolean }) {
  if (!events.length) {
    return <CenteredNote title="No audit events yet">Changes to this item will be listed here as they happen.</CenteredNote>;
  }
  const th = 'sticky top-0 z-10 border-b border-gray-200 bg-gray-50 px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400';
  const td = 'border-b border-gray-100 px-4 py-2 align-top dark:border-gray-700/70';
  // No wrapper: the pane itself scrolls both ways, so the sticky header sticks.
  return (
    <>
      <table className="w-full min-w-[34rem] border-separate border-spacing-0 text-sm">
        <thead>
          <tr>
            <th scope="col" className={`${th} w-36`}>When</th>
            <th scope="col" className={`${th} w-40`}>Who</th>
            <th scope="col" className={th}>Change</th>
            <th scope="col" className={`${th} w-20 text-right`}>Version</th>
          </tr>
        </thead>
        <tbody>
          {events.map((ev) => {
            const ago = recentAgo(ev.occurred_at);
            const whenTitle = Number.isNaN(new Date(ev.occurred_at).getTime()) ? undefined : new Date(ev.occurred_at).toLocaleString();
            return (
              <tr key={ev.id} data-audit-row="" className="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/40">
                <td className={`${td} whitespace-nowrap text-xs tabular-nums text-gray-700 dark:text-gray-300`} title={whenTitle}>
                  <div>{dateTime(ev.occurred_at)}</div>
                  {ago && <div className="text-gray-500 dark:text-gray-400">{ago}</div>}
                </td>
                <td className={`${td} text-gray-900 dark:text-gray-100`}>
                  {ev.user_name
                    ? <span className="font-medium">{ev.user_name}</span>
                    : <span className="text-gray-500 dark:text-gray-400">System</span>}
                </td>
                <td className={`${td} text-gray-700 dark:text-gray-300`}>
                  <span className="break-words">{ev.summary}</span>
                  {ev.source === 'derived' && (
                    <span
                      title={DERIVED_HINT}
                      className="ml-2 inline-flex cursor-help items-center gap-1 whitespace-nowrap align-middle text-[11px] text-gray-500 dark:text-gray-400"
                    >
                      <Info className="h-3 w-3" aria-hidden="true" />
                      from version record
                      <span className="sr-only">. {DERIVED_HINT}</span>
                    </span>
                  )}
                </td>
                <td className={`${td} text-right`}>
                  {ev.version != null && <VersionChip version={ev.version} />}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {truncated && (
        <p className="flex items-center gap-1.5 px-4 py-3 text-xs text-gray-600 dark:text-gray-400">
          <Info className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
          Showing the {events.length.toLocaleString()} most recent events. Older events aren&rsquo;t listed.
        </p>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Modal
// ---------------------------------------------------------------------------

const stopClick = (e: SyntheticEvent) => e.stopPropagation();
const stopKeys = (e: ReactKeyboardEvent) => {
  // Escape must still reach the Modal's document-level listener.
  if (e.key !== 'Escape') e.stopPropagation();
};

export default function VersionHistoryModal({
  isOpen,
  onClose,
  kind,
  params,
  title,
  initialVersionId,
}: VersionHistoryModalProps) {
  const [data, setData] = useState<VersionHistoryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [tab, setTab] = useState<Tab>('compare');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [olderId, setOlderId] = useState<string | null>(null);
  const [newerId, setNewerId] = useState<string | null>(null);
  const [copied, setCopied] = useState<'ok' | 'fail' | null>(null);
  const rowRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Callers pass params inline; key the fetch on their content, not identity.
  const paramsKey = JSON.stringify(params ?? {});

  useEffect(() => {
    if (!isOpen) {
      // Closed: drop the last result so a reopen never flashes stale versions.
      setData(null);
      setError(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    setCopied(null);
    const query = { kind, ...(JSON.parse(paramsKey) as VersionHistoryParams) };
    api.get<VersionHistoryResponse>('/api/crm/version-history', { params: query })
      .then((res) => {
        if (cancelled) return;
        const body = res.data;
        const versions = Array.isArray(body?.versions) ? body.versions : [];
        const audit = Array.isArray(body?.audit) ? body.audit : [];
        // Defaults skip discarded drafts: "newest vs the one before it" means
        // the newest real version, not an abandoned AI draft on top of the list.
        const newest = defaultNewest(versions)?.id ?? null;
        const previous = defaultPrevious(versions, newest)?.id ?? null;
        const initial = initialVersionId && versions.some((v) => v.id === initialVersionId)
          ? initialVersionId
          : (previous ?? newest);
        setData({ ...body, versions, audit, audit_truncated: Boolean(body?.audit_truncated) });
        setSelectedId(initial);
        setNewerId(newest);
        setOlderId(initial !== newest ? initial : (previous ?? newest));
        setTab(versions.length > 1 ? 'compare' : 'view');
      })
      .catch((e) => { if (!cancelled) setError(errMsg(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, kind, paramsKey, initialVersionId, reloadKey]);

  useEffect(() => () => { if (copyTimer.current) clearTimeout(copyTimer.current); }, []);

  const versions = useMemo(() => data?.versions ?? [], [data]);
  const byId = useMemo(() => new Map(versions.map((v) => [v.id, v])), [versions]);
  const selected = (selectedId && byId.get(selectedId)) || versions[0] || null;
  const older = (olderId && byId.get(olderId)) || null;
  const newer = (newerId && byId.get(newerId)) || null;
  const newest = defaultNewest(versions);

  // Keep the preselected row in view once the list renders.
  useEffect(() => {
    if (!data || !selectedId) return;
    rowRefs.current[selectedId]?.scrollIntoView?.({ block: 'nearest' });
    // Only on load — later selections are made by the user, in view already.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const selectVersion = useCallback((id: string) => {
    setSelectedId(id);
    if (tab === 'compare') setOlderId(id);
  }, [tab]);

  const changeTab = (next: Tab) => {
    if (next === 'compare' && selectedId) {
      // The version under review becomes the Older side, unless it IS the Newer
      // one — then the list highlight follows Older instead.
      if (selectedId !== newerId) setOlderId(selectedId);
      else if (olderId) setSelectedId(olderId);
    }
    setTab(next);
  };

  const compareWithNewest = () => {
    if (!selected || !newest || selected.id === newest.id) return;
    setOlderId(selected.id);
    setNewerId(newest.id);
    setTab('compare');
  };

  const swap = () => {
    setOlderId(newerId);
    setNewerId(olderId);
    if (newerId) setSelectedId(newerId);
  };

  const copyText = async () => {
    if (!selected) return;
    try {
      await navigator.clipboard.writeText(selected.text ?? '');
      setCopied('ok');
    } catch {
      setCopied('fail');
    }
    if (copyTimer.current) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopied(null), 1600);
  };

  const onListKeyDown = (e: ReactKeyboardEvent<HTMLUListElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Home' && e.key !== 'End') return;
    if (!versions.length) return;
    e.preventDefault();
    const cur = versions.findIndex((v) => v.id === selected?.id);
    let next = cur;
    if (e.key === 'ArrowDown') next = Math.min(versions.length - 1, cur + 1);
    else if (e.key === 'ArrowUp') next = Math.max(0, cur - 1);
    else if (e.key === 'Home') next = 0;
    else next = versions.length - 1;
    const id = versions[next].id;
    selectVersion(id);
    rowRefs.current[id]?.focus();
  };

  const onTabKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const i = TABS.findIndex((t) => t.id === tab);
    const next = TABS[(i + (e.key === 'ArrowRight' ? 1 : TABS.length - 1)) % TABS.length].id;
    changeTab(next);
    document.getElementById(`vh-tab-${next}`)?.focus();
  };

  if (!isOpen) return null;

  const name = data?.title || title;
  const heading = name ? `Version history · ${name}` : 'Version history';
  const auditCount = data?.audit.length ?? 0;

  // ---- Right-pane bodies -------------------------------------------------

  const viewPane = selected ? (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-shrink-0 flex-wrap items-center justify-between gap-2 border-b border-gray-200 px-4 py-2 dark:border-gray-700">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{selected.label}</span>
          <StatePill entry={selected} />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={compareWithNewest}
            disabled={!newest || selected.id === newest.id}
            title={newest && selected.id === newest.id ? 'This is the newest version' : `Compare ${selected.label} with ${newest?.label ?? 'the newest version'}`}
            className={btnCls}
          >
            <GitCompareArrows className="h-4 w-4" aria-hidden="true" /> Compare with newest
          </button>
          <button type="button" onClick={copyText} className={btnCls} aria-live="polite">
            {copied === 'ok'
              ? (<><Check className="h-4 w-4 text-green-600 dark:text-green-400" aria-hidden="true" /> Copied</>)
              : copied === 'fail'
                ? (<><Copy className="h-4 w-4" aria-hidden="true" /> Copy failed</>)
                : (<><Copy className="h-4 w-4" aria-hidden="true" /> Copy text</>)}
          </button>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3 sm:px-5 sm:py-4">
        <VersionMeta entry={selected} />
        {!selected.text ? (
          <p className="text-sm italic text-gray-500 dark:text-gray-400">This version has no content.</p>
        ) : data?.format === 'outline' ? (
          <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-6 text-gray-800 dark:text-gray-200">{selected.text}</pre>
        ) : (
          <MarkdownText text={selected.text} />
        )}
      </div>
    </div>
  ) : null;

  const comparePane = (
    <div className="flex min-h-0 flex-1 flex-col">
      {versions.length < 2 ? (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <CenteredNote title="Nothing to compare yet">
            There is only one version so far. When a new version is saved or published, you can compare the two here.
          </CenteredNote>
        </div>
      ) : (
        <>
          <div className="flex flex-shrink-0 flex-wrap items-center gap-2 border-b border-gray-200 px-4 py-2 dark:border-gray-700">
            <label className="flex min-w-0 flex-1 items-center gap-2 sm:flex-none">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Older</span>
              <select
                value={olderId ?? ''}
                onChange={(e) => { setOlderId(e.target.value); setSelectedId(e.target.value); }}
                className={`${fieldCls} min-w-0 flex-1 sm:w-64 sm:flex-none xl:w-80`}
              >
                {versions.map((v) => <option key={v.id} value={v.id}>{optionLabel(v)}</option>)}
              </select>
            </label>
            <button
              type="button"
              onClick={swap}
              aria-label="Swap older and newer"
              title="Swap older and newer"
              className={iconBtnCls}
            >
              <ArrowLeftRight className="h-4 w-4" aria-hidden="true" />
            </button>
            <label className="flex min-w-0 flex-1 items-center gap-2 sm:flex-none">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Newer</span>
              <select
                value={newerId ?? ''}
                onChange={(e) => setNewerId(e.target.value)}
                className={`${fieldCls} min-w-0 flex-1 sm:w-64 sm:flex-none xl:w-80`}
              >
                {versions.map((v) => <option key={v.id} value={v.id}>{optionLabel(v)}</option>)}
              </select>
            </label>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {!older || !newer ? null : older.id === newer.id ? (
              <CenteredNote title="Same version on both sides">
                Pick two different versions to see what changed between them.
              </CenteredNote>
            ) : (
              <VersionDiffView
                flush
                oldText={older.text ?? ''}
                newText={newer.text ?? ''}
                emptyMessage={`No differences between ${older.label} and ${newer.label}.`}
              />
            )}
          </div>
        </>
      )}
    </div>
  );

  const auditPane = (
    <div className="min-h-0 flex-1 overflow-auto">
      <AuditTrail events={data?.audit ?? []} truncated={Boolean(data?.audit_truncated)} />
    </div>
  );

  // ---- Frame ----------------------------------------------------------------

  let content: ReactNode;
  if (loading || (!data && !error)) {
    content = (
      <div className="flex flex-1 items-center justify-center" role="status">
        <Loader2 className="h-5 w-5 animate-spin text-primary-600 dark:text-primary-400" aria-hidden="true" />
        <span className="sr-only">Loading version history</span>
      </div>
    );
  } else if (error) {
    content = (
      <div className="flex-1 p-4 sm:p-6">
        <Alert
          tone="error"
          title="Couldn't load the version history"
          actions={(
            <button type="button" onClick={() => setReloadKey((k) => k + 1)} className={btnCls}>
              Retry
            </button>
          )}
        >
          {error}
        </Alert>
      </div>
    );
  } else if (!versions.length) {
    content = (
      <div className="flex-1">
        <CenteredNote title="No versions yet">Nothing has been saved or published here yet.</CenteredNote>
      </div>
    );
  } else {
    content = (
      <>
        <aside
          aria-label="Versions"
          className="flex max-h-44 flex-shrink-0 flex-col border-b border-gray-200 dark:border-gray-700 lg:max-h-none lg:w-72 lg:border-b-0 lg:border-r"
        >
          <div className="flex-shrink-0 px-3 pb-1.5 pt-3 text-[10px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400">
            Versions <span className="tabular-nums">({versions.length.toLocaleString()})</span>
          </div>
          <ul className="min-h-0 flex-1 overflow-y-auto pb-2" onKeyDown={onListKeyDown}>
            {versions.map((v) => {
              const isSel = selected?.id === v.id;
              const meta = metaLine(v);
              const sideTag = tab === 'compare' && versions.length > 1
                ? (v.id === olderId ? 'Older' : v.id === newerId ? 'Newer' : null)
                : null;
              return (
                <li key={v.id}>
                  <button
                    type="button"
                    ref={(el) => { rowRefs.current[v.id] = el; }}
                    onClick={() => selectVersion(v.id)}
                    aria-current={isSel ? 'true' : undefined}
                    className={`block w-full border-l-2 px-3 py-2 text-left transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 ${
                      isSel
                        ? 'border-primary-600 bg-primary-50 dark:border-primary-400 dark:bg-primary-900/20'
                        : 'border-transparent hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      <span className={`min-w-0 truncate text-sm font-medium ${isSel ? 'text-primary-700 dark:text-primary-300' : 'text-gray-900 dark:text-gray-100'}`}>
                        {v.label}
                      </span>
                      <StatePill entry={v} />
                      {sideTag && (
                        <span className="ml-auto flex-shrink-0 text-[10px] font-semibold uppercase tracking-widest text-primary-700 dark:text-primary-400">
                          {sideTag}
                        </span>
                      )}
                    </span>
                    {meta && (
                      <span className="mt-0.5 block truncate text-xs text-gray-600 dark:text-gray-400" title={meta}>{meta}</span>
                    )}
                    {v.origin && (
                      <span className="mt-0.5 block truncate text-xs text-gray-500 dark:text-gray-400" title={v.origin}>{v.origin}</span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>
        <section className="flex min-h-0 min-w-0 flex-1 flex-col" aria-label="Version details">
          <div
            role="tablist"
            aria-label="Version history views"
            onKeyDown={onTabKeyDown}
            className="flex flex-shrink-0 items-end gap-1 border-b border-gray-200 px-3 dark:border-gray-700"
          >
            {TABS.map((t) => {
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  id={`vh-tab-${t.id}`}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  aria-controls={active ? 'vh-panel' : undefined}
                  tabIndex={active ? 0 : -1}
                  onClick={() => changeTab(t.id)}
                  className={`-mb-px inline-flex min-h-[44px] items-center gap-1.5 border-b-2 px-2.5 text-sm font-medium transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 sm:min-h-0 sm:py-2 ${
                    active
                      ? 'border-primary-600 text-primary-700 dark:border-primary-400 dark:text-primary-400'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-200'
                  }`}
                >
                  {t.label}
                  {t.id === 'audit' && auditCount > 0 && (
                    <span className="rounded-full bg-gray-100 px-1.5 text-[11px] font-medium tabular-nums text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                      {auditCount.toLocaleString()}{data?.audit_truncated ? '+' : ''}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <div
            role="tabpanel"
            id="vh-panel"
            aria-labelledby={`vh-tab-${tab}`}
            className="flex min-h-0 flex-1 flex-col"
          >
            {tab === 'view' ? viewPane : tab === 'compare' ? comparePane : auditPane}
          </div>
        </section>
      </>
    );
  }

  // The wrapper only stops propagation; the Modal is fixed-position and owns
  // overlay click / Escape / scroll lock.
  return createPortal(
    <div className="font-inter" onClick={stopClick} onKeyDown={stopKeys}>
      <Modal isOpen={isOpen} onClose={onClose} title={heading} maxWidth="max-w-6xl">
        {/* Bleed through the Modal body's padding; the height keeps the whole
            dialog inside a ~700px viewport so only the inner panes scroll. */}
        <div className="-m-4 flex h-[calc(100vh-10rem)] max-h-[680px] min-h-[320px] flex-col sm:-m-6 lg:flex-row">
          {content}
        </div>
      </Modal>
    </div>,
    document.body,
  );
}
